/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiProgressProps, useEuiTheme } from "@inscopix/ideas-eui";
import { useMemo } from "react";
import { uuid } from "utils/uuid";

const styles = {
  root: (size: string) => css`
    flex-shrink: 0;
    height: ${size};
    width: ${size};
  `,
};

type StepColor =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "accent"
  | "text";

export type StepStatus = "pending" | "active" | "complete" | "error";

const DEFAULT_STATUS_COLORS: Record<StepStatus, StepColor | null> = {
  pending: null, // unfilled track color
  active: "primary",
  complete: "success",
  error: "danger",
};

export interface StepProgressProps extends React.ComponentPropsWithoutRef<"svg"> {
  /** One entry per wedge, clockwise from 12 o'clock. Length sets the wedge count. */
  steps: StepStatus[];
  size?: EuiProgressProps["size"];
  /** Override the color for any status. null = unfilled track color. */
  statusColors?: Partial<Record<StepStatus, StepColor | null>>;
}

// Fraction of the circle (0 = 12 o'clock, clockwise) -> SVG coords (y down)
const pointAt = (fraction: number) => {
  const angle = fraction * Math.PI * 2;
  return { x: Math.sin(angle), y: -Math.cos(angle) };
};

const wedgePath = (startFraction: number, endFraction: number) => {
  const start = pointAt(startFraction);
  const end = pointAt(endFraction);
  const largeArcFlag = endFraction - startFraction > 0.5 ? 1 : 0;
  return `M 0 0 L ${start.x} ${start.y} A 1 1 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
};

export const StepProgress = ({
  steps: _steps,
  size = "m",
  statusColors,
  ...props
}: StepProgressProps) => {
  const { euiTheme } = useEuiTheme();
  const colorMap = { ...DEFAULT_STATUS_COLORS, ...statusColors };

  const fillFor = (status: StepStatus) => {
    const key = colorMap[status];
    return key !== null ? euiTheme.colors[key] : euiTheme.colors.lightShade;
  };

  const steps = useMemo(
    () => _steps.map((s) => ({ status: s, id: uuid() })),
    [_steps],
  );
  const count = steps.length;

  return (
    <svg
      css={styles.root(euiTheme.size[size])}
      viewBox="-1 -1 2 2"
      role="img"
      {...props}
      cursor={props.onClick !== undefined ? "pointer" : undefined}
    >
      <circle cx={0} cy={0} r={1} fill={euiTheme.colors.lightShade} />

      {count === 1 && (
        <circle cx={0} cy={0} r={1} fill={fillFor(steps[0].status)} />
      )}

      {count > 1 &&
        steps.map((s, i) => (
          <path
            key={s.id}
            d={wedgePath(i / count, (i + 1) / count)}
            fill={fillFor(s.status)}
            stroke={euiTheme.colors.emptyShade}
            strokeWidth={0.03}
            strokeLinejoin="round"
          />
        ))}
    </svg>
  );
};
